import React, { useEffect, useState, useMemo } from "react";
import { Drawer, Form, Input, Button, Select, Table, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import API from "../../api"; // Your API instance

const { Option } = Select;

const OrderForm = ({ open, setOpen, data, onSave }) => {
  const [form] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [store, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsInOrder, setProductsInOrder] = useState([]);

  // Fetch store and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [branchResponse, productResponse] = await Promise?.all([
          API.get("/store"),
          API.get("/products"),
        ]);
        setBranches(branchResponse.data);
        setProducts(productResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to fetch data.");
      }
    };
    fetchData();
  }, []);

  // Populate form and products when editing
  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        orderType: data?.orderType,
        branchId: data.branchId?._id,
        totalAmount: calculateTotalAmount(data?.products || []),
        status: data?.status,
      });
      setProductsInOrder(data?.products || []);
    } else {
      form.resetFields();
      setProductsInOrder([]);
    }
  }, [data, form]);

  const calculateTotalAmount = (products) =>
    products.reduce((total, product) => total + product?.totalPrice, 0);

  // Memoized total amount
  const totalAmount = useMemo(
    () => calculateTotalAmount(productsInOrder),
    [productsInOrder]
  );

  // Update form totalAmount field when totalAmount changes
  useEffect(() => {
    form.setFieldsValue({ totalAmount });
  }, [totalAmount, form]);

  const handleAddProduct = (product) => {
    setProductsInOrder((prev) => [
      ...prev,
      {
        productId: product?._id,
        quantity: 1,
        unitPrice: product?.unitPrice,
        totalPrice: product?.unitPrice,
      },
    ]);
    setSelectedProduct(null);
  };

  const handleRemoveProduct = (productId) => {
    setProductsInOrder((prev) =>
      prev.filter((item) => item?.productId !== productId)
    );
  };

  const handleQuantityChange = (productId, quantity) => {
    setProductsInOrder((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? { ...item, quantity, totalPrice: item?.unitPrice * quantity }
          : item
      )
    );
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const orderData = { ...values, products: productsInOrder };
      if (data) {
        await API.put(`/orders/${data?._id}`, orderData);
        message.success("Order updated successfully!");
      } else {
        await API.post("/orders", orderData);
        message.success("Order created successfully!");
      }
      onSave();
      setOpen(false);
    } catch (error) {
      console.error("Error saving order:", error);
      message.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const availableProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          !productsInOrder.some((item) => item?.productId === product?._id)
      ),
    [products, productsInOrder]
  );

  const columns = [
    {
      title: "Product",
      dataIndex: "productId",
      key: "productId",
      render: (productId) => {
        const product = data
          ? products.find((prod) => prod?._id === productId?._id)
          : products.find((prod) => prod?._id === productId);
        return product ? product.name : "Unknown";
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <Input
          type="number"
          value={record?.quantity}
          min={1}
          disabled={data}
          onChange={(e) =>
            handleQuantityChange(record?.productId, Number(e.target.value))
          }
        />
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          danger
          onClick={() => handleRemoveProduct(record.productId)}
          disabled={!!data?.status}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Drawer
      title={data ? "Edit Order" : "Add Order"}
      width={600}
      onClose={() => setOpen(false)}
      open={open}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="orderType"
          label="Order Type"
          rules={[{ required: true, message: "Order type is required." }]}
        >
          <Select placeholder="Select Order Type" disabled={data}>
            <Option value="sale">Sale</Option>
            <Option value="purchase">Purchase</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="branchId"
          label="Branch"
          rules={[{ required: true, message: "Please select a branch." }]}
        >
          <Select placeholder="Select Branch" disabled={data}>
            {store.map((branch) => (
              <Option key={branch?._id} value={branch?._id}>
                {branch?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select placeholder="Select Status">
            <Option value="pending">Pending</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </Form.Item>

        <Form.Item name="totalAmount" label="Total Amount">
          <Input type="number" value={totalAmount} readOnly disabled={data} />
        </Form.Item>

        <Table
          columns={columns}
          dataSource={productsInOrder}
          rowKey="productId"
          pagination={false}
          footer={() => (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <Select
                placeholder="Select Product"
                value={selectedProduct?.id}
                onChange={(value) => {
                  const product = products.find((prod) => prod?._id === value);
                  setSelectedProduct(product);
                }}
                style={{ flex: 1 }}
                disabled={data}
              >
                {availableProducts.map((product) => (
                  <Option key={product?._id} value={product?._id}>
                    {product?.name}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  selectedProduct && handleAddProduct(selectedProduct)
                }
                disabled={data}
              >
                Add Product
              </Button>
            </div>
          )}
        />

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} block>
            {data ? "Update Order" : "Create Order"}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default OrderForm;
